import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { oracleCardsService } from '../services';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';

const AddEditOracleCardPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [description, setDescription] = useState('');

  const [frontImageFile, setFrontImageFile] = useState<File | null>(null);
  const [backImageFile, setBackImageFile] = useState<File | null>(null);
  const [frontImagePreview, setFrontImagePreview] = useState<string>('');
  const [backImagePreview, setBackImagePreview] = useState<string>('');
  const [existingFrontImageUrl, setExistingFrontImageUrl] = useState<string>('');
  const [existingBackImageUrl, setExistingBackImageUrl] = useState<string>('');

  useEffect(() => {
    if (id) loadCard();
  }, [id]);

  const loadCard = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await oracleCardsService.getOne(id);
      setTitle(data.title || '');
      setShortDesc(data.shortDesc || '');
      setDescription(data.description || '');
      setExistingFrontImageUrl(data.frontImage || '');
      setExistingBackImageUrl(data.backImage || '');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load card');
      setError('Failed to load card');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps: getFrontImageProps, getInputProps: getFrontImageInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    onDrop: (accepted) => {
      if (accepted.length > 0) {
        setFrontImageFile(accepted[0]);
        setFrontImagePreview(URL.createObjectURL(accepted[0]));
      }
    },
  });

  const { getRootProps: getBackImageProps, getInputProps: getBackImageInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    onDrop: (accepted) => {
      if (accepted.length > 0) {
        setBackImageFile(accepted[0]);
        setBackImagePreview(URL.createObjectURL(accepted[0]));
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!isEdit && (!frontImageFile || !backImageFile)) {
      toast.error('Front and back images are required');
      return;
    }
    if (isEdit && !frontImageFile && !existingFrontImageUrl) {
      toast.error('Front image is required');
      return;
    }
    if (isEdit && !backImageFile && !existingBackImageUrl) {
      toast.error('Back image is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      if (shortDesc) formData.append('shortDesc', shortDesc.trim());
      if (description) formData.append('description', description.trim());
      if (frontImageFile) formData.append('frontImage', frontImageFile);
      if (backImageFile) formData.append('backImage', backImageFile);

      if (isEdit && id) {
        await oracleCardsService.update(id, formData);
        toast.success('Card updated');
      } else {
        await oracleCardsService.create(formData);
        toast.success('Card created');
      }
      navigate('/dashboard/oracle-cards');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save card';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading card...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? 'Edit Card' : 'Add Card'}
          pageTitle="Oracle Cards"
        />
        {error && (
          <Alert color="danger" toggle={() => setError('')}>
            {error}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-4">
                <i className="ri-information-line me-2"></i>
                Card details
              </h5>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Title *</Label>
                    <Input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Strength"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Short description</Label>
                    <Input
                      type="text"
                      value={shortDesc}
                      onChange={(e) => setShortDesc(e.target.value)}
                      placeholder="Brief line for list view"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Description / affirmation</Label>
                    <Input
                      type="textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="Full text or affirmation"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Front image *</Label>
                    <p className="text-muted small mb-2">JPEG, PNG or WEBP. Shown on card front.</p>
                    {(frontImagePreview || existingFrontImageUrl) && (
                      <div className="mb-2">
                        <img
                          src={frontImagePreview || existingFrontImageUrl}
                          alt="Card front"
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
                        />
                      </div>
                    )}
                    <div
                      {...getFrontImageProps()}
                      className="border rounded p-3 text-center"
                      style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                    >
                      <input {...getFrontImageInputProps()} />
                      <i className="ri-image-add-line fs-2 text-muted"></i>
                      <p className="mb-0 small text-muted">Drop front image or click to select</p>
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>Back image *</Label>
                    <p className="text-muted small mb-2">JPEG, PNG or WEBP. Shown after card flip.</p>
                    {(backImagePreview || existingBackImageUrl) && (
                      <div className="mb-2">
                        <img
                          src={backImagePreview || existingBackImageUrl}
                          alt="Card back"
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
                        />
                      </div>
                    )}
                    <div
                      {...getBackImageProps()}
                      className="border rounded p-3 text-center"
                      style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                    >
                      <input {...getBackImageInputProps()} />
                      <i className="ri-image-add-line fs-2 text-muted"></i>
                      <p className="mb-0 small text-muted">Drop back image or click to select</p>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <div className="d-flex gap-2 mt-3">
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : (isEdit ? 'Update card' : 'Create card')}
                </Button>
                <Button type="button" color="light" onClick={() => navigate('/dashboard/oracle-cards')}>
                  Cancel
                </Button>
              </div>
            </CardBody>
          </Card>
        </Form>
      </Container>
    </div>
  );
};

export default AddEditOracleCardPage;
